import { defineComponent as _defineComponent } from 'vue'
import type { Maybe } from '@/helpers/types';
import ProviderLogoVideoControls from '@/features/sponsored-recommendations/components/ProviderLogoVideoControls.vue';
import PlayerProgressBar from '@/features/sponsored-recommendations/components/PlayerProgressBar.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useDocumentVisibility } from '@vueuse/core';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { SraEventAction, SraPlacement } from '@/@types/graphql-types';

import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { SingleTitleSR } from '@/features/sponsored-recommendations/types';

import SRCompanion from '@/features/sponsored-recommendations/components/SRCompanion.vue';
import { useBackendTracking } from '@/helpers/composables/useBackendTracking';

/** Viewing thresholds in percentages at which we want to track. */
type TrackingWatchedPointsInPercentage = (typeof TrackingWatchedPointsInPercentage)[number];
type TrackingWatchedPointsInSeconds = (typeof TrackingWatchedPointsInSeconds)[number];

export default /*#__PURE__*/_defineComponent({
  __name: 'SponsoredRecommendationBackdrop',
  props: {
    sponsoredAd: null,
    isMuted: { type: Boolean, default: false },
    lazy: { type: Boolean, default: true },
    showOverlay: { type: Boolean, default: false },
    additionalContexts: { default: () => [] },
    sraPlacement: null,
    showPlayerProgress: { type: Boolean },
    showCountDownTimer: { type: Boolean },
    showCountdownText: { type: Boolean },
    showProviderIcon: { type: Boolean },
    hideContent: { type: Boolean }
  },
  emits: ["onPosterClickOut", "imageError", "onVideoEnded"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'onPosterClickOut'): void;
	(e: 'imageError', src: Maybe<string>): void;
	(e: 'onVideoEnded', hasVideoEnded: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { sponsoredAd: SponsoredAdFragment, isMuted: boolean, lazy: boolean, showOverlay: boolean, additionalContexts: SnowplowContext[], sraPlacement?: SraPlacement, showPlayerProgress?: boolean, showCountDownTimer?: boolean, showCountdownText?: boolean, showProviderIcon?: boolean, hideContent?: boolean };

const TrackingWatchedPointsInPercentage = [0, 25, 50, 75, 90, 100] as const;

/** Viewing thresholds in seconds at which we want to track. */
const TrackingWatchedPointsInSeconds = [2, 6, 15] as const;





function imageError(src: Maybe<string>) {
	emits('imageError', src);
}

function onPosterClickOut() {
	emits('onPosterClickOut');
}

const { trackSraEvent } = useBackendTracking();
const visibility = useDocumentVisibility();

const sponsoredCampaign = computed(() => props.sponsoredAd?.campaign);
const campaignNode = computed<SingleTitleSR | null>(
	() => (sponsoredCampaign.value?.node ?? null) as SingleTitleSR | null
);

/* VIDEO TRACKING */
const videoElement = ref<HTMLVideoElement | null>(null);

/* Watch Document Visibility */
watch(visibility, (current, previous) => {
	if (videoElement && current === 'visible' && previous === 'hidden') {
		videoElement.value?.play();
		return;
	}

	if (videoElement && current === 'hidden' && previous === 'visible') {
		videoElement.value?.pause();
		return;
	}
});

/* VIDEO CONTROL*/
const isPaused = ref(false);
const isMuted = ref(true);
const currentTime = ref(0);

watch(isMuted, newMuted => {
	if (videoElement.value) {
		videoElement.value.muted = newMuted;
	}
});

watch(isPaused, newPaused => {
	if (videoElement.value) {
		if (newPaused) {
			videoElement.value.pause();
		} else {
			videoElement.value.play();
		}
	}
});

function onVideoStateChanged(eventName: string) {
	if (eventName !== 'play') return;
	setTimeout(() => {
		if (!videoElement.value) return;
		isPaused.value ? videoElement.value.pause() : videoElement.value.play();
	}, 0);
}

function onVisibilityPlaybackControl(isVisible: boolean) {
	if (!videoElement.value) return;
	isVisible ? videoElement.value.play() : videoElement.value.pause();
}

function toggleMute() {
	isMuted.value = !isMuted.value;

	if (videoElement.value) {
		videoElement.value.muted = isMuted.value;
	}

	if (!campaignNode.value) {
		return;
	}

	trackSraEvent({
		action: SraEventAction.TrailerToggleMute,
		sponsoredAd: () => props.sponsoredAd,
		progress: isMuted.value ? 1 : 0,
	});
}

function togglePlayPause() {
	isPaused.value = !isPaused.value;

	if (!videoElement.value) return;

	isPaused.value ? videoElement.value.pause() : videoElement.value.play();

	if (!campaignNode.value) {
		return;
	}

	// Track the play/pause event only for Backdrop placement
	trackSraEvent({
		action: SraEventAction.TrailerTogglePlay,
		sponsoredAd: () => props.sponsoredAd,
		progress: isPaused.value ? 1 : 0,
	});
}

/* Track Duration Play */
const videoDuration = ref(0);
const lastTrackedPointPercentage = ref<TrackingWatchedPointsInPercentage | -1>(-1);
const lastTrackedPointSeconds = ref<TrackingWatchedPointsInSeconds | -1>(-1);

function setVideoDuration() {
	if (!videoElement.value) return;
	videoDuration.value = videoElement.value.duration;
}

function onTimeUpdate() {
	currentTime.value = videoElement.value?.currentTime ?? 0;

	/** Percentage Tracking */
	if (lastTrackedPointPercentage.value < 100 && videoDuration.value > 0) {
		const percentageViewed = Math.ceil((currentTime.value / videoDuration.value) * 100);
		const nextTrackingPointPercent = TrackingWatchedPointsInPercentage.find(
			point => point > lastTrackedPointPercentage.value
		);

		if (nextTrackingPointPercent != null && percentageViewed >= nextTrackingPointPercent) {
			lastTrackedPointPercentage.value = nextTrackingPointPercent;

			trackSraEvent({
				action: SraEventAction.VideoView,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointPercent,
			});
		}
	}

	/** Seconds Tracking */
	if (lastTrackedPointSeconds.value < 15) {
		const secondsViewed = Math.floor(currentTime.value);
		const nextTrackingPointSeconds = TrackingWatchedPointsInSeconds.find(
			point => point > lastTrackedPointSeconds.value
		);

		if (nextTrackingPointSeconds != null && secondsViewed >= nextTrackingPointSeconds) {
			lastTrackedPointSeconds.value = nextTrackingPointSeconds;

			trackSraEvent({
				action: SraEventAction.VideoViewSeconds,
				sponsoredAd: () => props.sponsoredAd,
				progress: nextTrackingPointSeconds,
			});
		}
	}
}

/**
 * `this.onTimeUpdate` doesn't always fire at 100 before restarting the loop.
 * So we trigger it manually to guarantee it gets tracked.
 */
function onVideoEnded() {
	if (!videoElement.value) return;

	emits('onVideoEnded', true);

	// Replay the video
	//videoElement.value.play();
}

// Set the initial muted state based on the prop
onMounted(() => {
	if (props.sraPlacement === 'BACKDROP_VIDEO') {
		isMuted.value = false;
		if (videoElement.value) {
			videoElement.value.muted = false;
		}
	}
});

return { __sfc: true,TrackingWatchedPointsInPercentage, TrackingWatchedPointsInSeconds, props, emits, imageError, onPosterClickOut, trackSraEvent, visibility, sponsoredCampaign, campaignNode, videoElement, isPaused, isMuted, currentTime, onVideoStateChanged, onVisibilityPlaybackControl, toggleMute, togglePlayPause, videoDuration, lastTrackedPointPercentage, lastTrackedPointSeconds, setVideoDuration, onTimeUpdate, onVideoEnded, ProviderLogoVideoControls, PlayerProgressBar, SRCompanion }
}

})